import { featureOn, roleCan, roleOf, termsComplete, TERMS_VERSION, type Action, type Role } from "@szerviz/core";
import { LANGUAGES } from "@szerviz/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageSelect } from "./components/LanguageSelect";
import { Logo } from "./components/Logo";
import { NavIcon } from "./components/Icon";
import { NewVersionBar, type VersionWatchConfig } from "./components/NewVersionBar";
import { QuoteNotices } from "./components/QuoteNotices";
import { IntakeNotices } from "./components/Intake";
import { TermsGate } from "./components/TermsGate";
import { AppContext } from "./context";
import type { AuthIntent, BillingStatus, DataStore, MfaRequirement, MfaStatus, MySubscription, SessionUser, UserPrefs, Workshop } from "./data";
import { codeNeeded } from "./mfaTrust";
import { checkLeave } from "./leaveGuard";
import { MfaCode, MfaSetup, StepUpForm } from "./screens/Mfa";
import { StepUpDialog } from "./components/StepUpDialog";
import { CreateWorkshop } from "./screens/CreateWorkshop";
import { Customers } from "./screens/Customers";
import { Feedback } from "./screens/Feedback";
import { Home } from "./screens/Home";
import { Import } from "./screens/Import";
import { Inventory } from "./screens/Inventory";
import { Legal } from "./screens/Legal";
import { Login } from "./screens/Login";
import { PhoneSetup, phoneSetupTokenFromHash } from "./screens/PhoneSetup";
import { Operator } from "./screens/Operator";
import { QuotePage } from "./screens/QuotePage";
import { IntakePage } from "./screens/IntakePage";
import { ReadyPage } from "./screens/ReadyPage";
import { SetPassword } from "./screens/SetPassword";
import { Settings } from "./screens/Settings";
import { Subscription, TrialReminder } from "./screens/Subscription";
import { PlanTag } from "./components/TrialGuide";
import { Reminders } from "./screens/Reminders";
import { Vehicles } from "./screens/Vehicles";
import { WorkOrders } from "./screens/WorkOrders";
import { APP_VERSION, versionCheckEnabled } from "./version";
import "./styles.css";

export const NAV = ["dashboard", "workOrders", "customers", "vehicles", "reminders", "inventory", "invoices", "import", "settings"] as const;
/** Menu items shown only while their feature is on. */
const NAV_FEATURE: Partial<Record<(typeof NAV)[number], string>> = { reminders: "reminders" };
/** Pages only for who may do that work (three levels, owner 2026-11-04): a mechanic does not see them. */
const NAV_ACTION: Partial<Record<(typeof NAV)[number], Action>> = { reminders: "reminders", inventory: "stock", invoices: "invoice", import: "import" };
/** Pages of a Profi feature: a small "Profi" tag in the menu while the trial runs. */
const NAV_PLAN_TAG: Partial<Record<(typeof NAV)[number], string>> = { inventory: "stock", reminders: "reminders" };
/** Pages reached by address but not listed in the main menu. */
const EXTRA_PAGES = ["legal", "operator", "feedback", "subscription"] as const;
export type Page = (typeof NAV)[number] | (typeof EXTRA_PAGES)[number];
/** The phone's bottom bar: the pages used in the workshop; the rest is under "Menu". */
const PHONE_NAV = ["dashboard", "workOrders", "vehicles", "customers"] as const;

/** "#vehicles/<id>" → page and record. */
/** Signing out forgets the page, so the next user starts on their own start page (a mechanic on his own work orders). */
function signOutFresh(store: DataStore) {
  globalThis.history?.replaceState?.(null, "", globalThis.location?.pathname ?? "/");
  return store.signOut();
}

export const routeFromHash = (): { page: Page; id?: string } => {
  const [p, id] = (globalThis.location?.hash ?? "").replace("#", "").split("/");
  const page = [...NAV, ...EXTRA_PAGES].includes(p as Page) ? (p as Page) : "dashboard";
  return id ? { page, id } : { page };
};

const HELP_ASK_AFTER_DAYS = 7;

/** Same width as the phone layout in styles.css. */
export const PHONE_QUERY = "(max-width: 760px)";

export function useMedia(query: string): boolean {
  const get = () => globalThis.matchMedia?.(query).matches ?? false;
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    const m = globalThis.matchMedia?.(query);
    if (!m) return;
    const on = () => setMatches(m.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, [query]);
  return matches;
}

/** "#quote/<token>": the customer's quote page, open without signing in. */
export const quoteTokenFromHash = (): string | null => {
  const m = /^#quote\/([0-9a-f]{16,})$/i.exec(globalThis.location?.hash ?? "");
  return m ? m[1]! : null;
};

/** "#intake/<token>": the customer's intake receipt page (photos, signature, objection), open without signing in. */
export const intakeTokenFromHash = (): string | null => {
  const m = /^#intake\/([0-9a-f]{64})$/i.exec(globalThis.location?.hash ?? "");
  return m ? m[1]! : null;
};

/** "#ready/<token>": the customer's "car ready" page (route, opening hours), open without signing in. */
export const readyTokenFromHash = (): string | null => {
  const m = /^#ready\/([0-9a-f]{24})$/i.exec(globalThis.location?.hash ?? "");
  return m ? m[1]!.toLowerCase() : null;
};

/** Production builds watch for a new version; dev, tests and the browser test build do not (src/version.ts). */
const DEFAULT_VERSION_WATCH: VersionWatchConfig | null = versionCheckEnabled() ? { current: APP_VERSION } : null;

export function App({
  store,
  authIntent = "none",
  versionWatch = DEFAULT_VERSION_WATCH,
}: {
  store: DataStore;
  authIntent?: AuthIntent;
  versionWatch?: VersionWatchConfig | null;
}) {
  // follow the address: a quote link opened in the same tab shows the customer's page
  const [, setHash] = useState(globalThis.location?.hash ?? "");
  useEffect(() => {
    const on = () => setHash(globalThis.location?.hash ?? "");
    globalThis.addEventListener?.("hashchange", on);
    return () => globalThis.removeEventListener?.("hashchange", on);
  }, []);
  const token = quoteTokenFromHash();
  if (token) return <QuotePage store={store} token={token} />;
  const intake = intakeTokenFromHash();
  if (intake) return <IntakePage store={store} token={intake} />;
  const ready = readyTokenFromHash();
  if (ready) return <ReadyPage store={store} token={ready} />;
  const setupToken = phoneSetupTokenFromHash();
  return (
    <>
      <NewVersionBar config={versionWatch} />
      {setupToken ? <PhoneSetup store={store} token={setupToken} /> : <SignedInApp store={store} authIntent={authIntent} />}
    </>
  );
}

export interface MfaInfo {
  status: MfaStatus;
  requirement: MfaRequirement;
  /** The server remembers this device for the user (the tick, 30 days): the code is not asked. */
  deviceOk?: boolean;
}

const NO_MFA: MfaInfo = { status: { factor: false, verified: false }, requirement: { required: false, reason: null, testExempt: false }, deviceOk: false };

function SignedInApp({ store, authIntent }: { store: DataStore; authIntent: AuthIntent }) {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);
  const [intent, setIntent] = useState<AuthIntent>(authIntent);
  const [workshop, setWorkshop] = useState<Workshop | null | undefined>(undefined);
  const [mfa, setMfa] = useState<MfaInfo | undefined>(undefined);
  // the code was given in this tab (the trust rules are checked when the app loads)
  const [codeDone, setCodeDone] = useState(false);
  // after a backup code: the sign in screen says what to do; then the new phone is set up
  const [recovered, setRecovered] = useState(false);
  const [setupSkipped, setSetupSkipped] = useState(false);

  const loadMfa = useCallback(async () => {
    try {
      const [status, requirement] = await Promise.all([store.mfaStatus(), store.mfaRequirement()]);
      // a remembered device is asked only when there is a code to skip
      const deviceOk = status.factor ? await store.mfaDeviceOk().catch(() => false) : false;
      setMfa({ status, requirement, deviceOk });
    } catch {
      // Auth not reachable for MFA: the database still refuses what needs the second step
      setMfa(NO_MFA);
    }
  }, [store]);

  const refresh = useCallback(async () => {
    const u = await store.currentUser();
    setUser(u);
    if (!u) {
      setMfa(undefined);
      setCodeDone(false);
      setWorkshop(null);
      return;
    }
    await loadMfa();
    setWorkshop((await store.myWorkshops())[0] ?? null);
  }, [store, loadMfa]);

  useEffect(() => {
    void refresh();
    return store.onAuthChange(() => void refresh());
  }, [store, refresh]);

  const signOut = () => void signOutFresh(store);
  if (user === undefined || (user && (workshop === undefined || mfa === undefined))) return <div className="loading" />;
  if (!user) return <Login store={store} notice={recovered ? "mfa.backupLogin.done" : null} />;
  if (intent !== "none") {
    return (
      <SetPassword
        store={store}
        intent={intent}
        onDone={() => {
          setIntent("none");
          globalThis.history?.replaceState(null, "", globalThis.location?.pathname ?? "/");
        }}
      />
    );
  }
  // two-step sign in: the code first, then (where required) setting it up; nothing else until then
  if (mfa && !codeDone && codeNeeded(user.id, mfa.status, mfa.deviceOk)) {
    return (
      <MfaCode
        store={store}
        userId={user.id}
        operator={mfa.requirement.reason === "operator"}
        onDone={() => {
          setCodeDone(true);
          void loadMfa();
        }}
        onRecovered={() => setRecovered(true)}
        onSignOut={signOut}
      />
    );
  }
  if (mfa && !mfa.status.factor && (mfa.requirement.required || (recovered && !setupSkipped))) {
    return (
      <MfaSetup
        store={store}
        userId={user.id}
        email={user.email}
        reason={mfa.requirement.reason}
        onDone={() => {
          setRecovered(false);
          setCodeDone(true);
          void loadMfa();
        }}
        onSkip={mfa.requirement.required ? undefined : () => setSetupSkipped(true)}
        onSignOut={signOut}
      />
    );
  }
  if (!workshop) {
    return (
      <CreateWorkshop
        store={store}
        onCreated={(w) => {
          setWorkshop(w);
          // the new owner must set up two-step sign in now
          void loadMfa();
        }}
      />
    );
  }
  return <Shell store={store} user={user} workshop={workshop} onWorkshop={setWorkshop} mfa={mfa ?? NO_MFA} reloadMfa={loadMfa} />;
}

function Shell({ store, user, workshop, onWorkshop, mfa, reloadMfa }: {
  store: DataStore; user: SessionUser; workshop: Workshop; onWorkshop: (w: Workshop) => void; mfa: MfaInfo; reloadMfa: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const [route, setRoute] = useState(routeFromHash);
  const [prefs, setPrefs] = useState<UserPrefs | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [termsOk, setTermsOk] = useState<boolean | null>(null);
  const [operator, setOperator] = useState(false);
  const [billing, setBilling] = useState<BillingStatus>("trial");
  const [subscription, setSubscription] = useState<MySubscription | null>(null);
  const phone = useMedia(PHONE_QUERY);
  // the page a report is about: the last one before the feedback page
  const [cameFrom, setCameFrom] = useState("#");
  useEffect(() => {
    if (route.page !== "feedback") setCameFrom(route.id ? `#${route.page}/${route.id}` : `#${route.page}`);
  }, [route]);

  useEffect(() => {
    void store.isPlatformAdmin().then(setOperator, () => setOperator(false));
    void store.myBillingStatus(workshop.id).then(setBilling, () => setBilling("trial"));
  }, [store, workshop.id]);
  // the trial reminder (owner) and the read only banner (everyone), read again when the page or the plan changes
  const reloadSubscription = useCallback(async () => {
    await store.mySubscription(workshop.id).then(setSubscription, () => setSubscription(null));
  }, [store, workshop.id]);
  useEffect(() => {
    if (!role) return;
    void reloadSubscription();
  }, [reloadSubscription, workshop.plan, workshop.features, role, route.page]);

  useEffect(() => {
    void store.termsAcceptances(workshop.id).then((a) => setTermsOk(termsComplete(a)));
  }, [store, workshop.id]);

  useEffect(() => {
    void store.listMembers(workshop.id).then((m) => setRole(roleOf(m.find((x) => x.userId === user.id)?.role)));
  }, [store, workshop.id, user.id]);
  // a mechanic signing in starts on "Saját munkalapjaim" (owner, 2026-11-04); a link to a page is kept
  const bare = () => ["", "#", "#dashboard"].includes(globalThis.location?.hash ?? "");
  const [startedBare] = useState(bare);
  const landed = useRef(false);
  useEffect(() => {
    if (!role || landed.current) return;
    landed.current = true;
    // only while still on the start page (the user may have moved on before the role arrived)
    if (role !== "mechanic" || !startedBare || !bare()) return;
    globalThis.history?.replaceState?.(globalThis.history.state, "", "#workOrders");
    setRoute({ page: "workOrders" });
  }, [role, startedBare]);
  const current = LANGUAGES.find((l) => l.code === i18n.language);

  // Leaving a screen with changes that cannot be saved (owner's bug report, 2026-11-04): the program asks in its own
  // dialog; what can be saved is saved by the form itself (leaveGuard.ts).
  const [leaveAsk, setLeaveAsk] = useState<{ fields: string[]; leave: () => void } | null>(null);
  const shownHash = useRef(globalThis.location?.hash ?? "");
  useEffect(() => {
    shownHash.current = route.id ? `#${route.page}/${route.id}` : `#${route.page}`;
  }, [route]);
  useEffect(() => {
    const onHash = () => {
      const target = globalThis.location?.hash ?? "";
      if (target === shownHash.current) return;
      const fields = checkLeave();
      if (fields.length === 0) return setRoute(routeFromHash());
      // stay on this screen until the user decides (the address goes back to it)
      const state = globalThis.history?.state;
      globalThis.history?.replaceState?.(state, "", shownHash.current || "#");
      setLeaveAsk({
        fields,
        leave: () => {
          globalThis.history?.replaceState?.(state, "", target);
          setRoute(routeFromHash());
        },
      });
    };
    globalThis.addEventListener?.("hashchange", onHash);
    globalThis.addEventListener?.("popstate", onHash);
    return () => {
      globalThis.removeEventListener?.("hashchange", onHash);
      globalThis.removeEventListener?.("popstate", onHash);
    };
  }, []);

  // New users start with the help on; the first time it shows, remember when.
  useEffect(() => {
    void store.getPrefs().then(async (p) => {
      setPrefs(p.helpEnabled && !p.helpSince ? await store.savePrefs({ helpSince: new Date().toISOString() }) : p);
    });
  }, [store]);

  // Every move inside the program is a history step that knows how deep it is, so "Back"
  // returns to the previous screen (customer -> customer list), not to the start page.
  const go = useCallback((page: string, id?: string) => {
    const hash = id ? `#${page}/${id}` : `#${page}`;
    if (hash !== shownHash.current) {
      const fields = checkLeave();
      if (fields.length > 0) return setLeaveAsk({ fields, leave: () => moveTo(page, id) });
    }
    moveTo(page, id);
  }, []);
  const moveTo = (page: string, id?: string) => {
    const hash = id ? `#${page}/${id}` : `#${page}`;
    if (globalThis.location && globalThis.location.hash !== hash) {
      const depth = Number((globalThis.history?.state as { garagevoDepth?: number } | null)?.garagevoDepth ?? 0);
      if (globalThis.history?.pushState) globalThis.history.pushState({ garagevoDepth: depth + 1 }, "", hash);
      else globalThis.location.hash = hash;
    }
    shownHash.current = hash;
    setRoute({ page: page as Page, ...(id ? { id } : {}) });
  };
  const back = useCallback((fallback: string) => {
    const depth = Number((globalThis.history?.state as { garagevoDepth?: number } | null)?.garagevoDepth ?? 0);
    if (depth > 0) globalThis.history.back();
    else go(fallback);
  }, [go]);
  const reloadWorkshop = useCallback(async () => {
    const w = (await store.myWorkshops()).find((x) => x.id === workshop.id);
    if (w) onWorkshop(w);
  }, [store, workshop.id, onWorkshop]);
  const setHelpEnabled = useCallback(async (on: boolean) => setPrefs(await store.savePrefs({ helpEnabled: on, helpAsked: true })), [store]);

  const helpEnabled = prefs?.helpEnabled ?? false;
  const askHelp =
    !!prefs?.helpEnabled && !prefs.helpAsked && !!prefs.helpSince && Date.now() - Date.parse(prefs.helpSince) >= HELP_ASK_AFTER_DAYS * 864e5;
  // a page this user may not use is not offered; its address leads to the start page
  const allowed = (key: string) => {
    const action = NAV_ACTION[key as (typeof NAV)[number]];
    return !action || roleCan(role, workshop.permissions, action);
  };
  const page = role && !allowed(route.page) ? "dashboard" : route.page;

  return (
    <AppContext.Provider value={{ store, workshop, reloadWorkshop, helpEnabled, setHelpEnabled, go, back, role, userId: user.id, mfa, reloadMfa, subscription, reloadSubscription }}>
      {leaveAsk && (
        <div className="leave-ask" role="alertdialog" aria-modal="true" aria-labelledby="leave-ask-title">
          <div className="leave-box">
            <h2 id="leave-ask-title">{t("leave.title")}</h2>
            <p>{t("leave.text", { fields: leaveAsk.fields.join(", ") })}</p>
            <div className="actions">
              <button type="button" className="primary" autoFocus onClick={() => setLeaveAsk(null)}>
                {t("leave.stay")}
              </button>
              <button type="button" className="danger" onClick={() => { const l = leaveAsk.leave; setLeaveAsk(null); l(); }}>
                {t("leave.go")}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* the operator pages are full width, without the workshop menu (owner, 2026-11-01) */}
      <div className={`shell${page === "operator" && operator ? " operator-mode" : ""}`}>
        <header className="topbar">
          <div className="brand">
            <Logo onDark />
            <span className="workshop">{workshop.name}</span>
          </div>
          <div className="topbar-right">
            <span className="user" title={user.email}>
              {user.email}
            </span>
            <button type="button" className="ghost signout" onClick={() => void signOutFresh(store)} aria-label={t("auth.signOut")} title={t("auth.signOut")}>
              <svg className="gi" viewBox="0 0 48 48" width="22" height="22" aria-hidden="true" focusable="false">
                <path d="M28 6H40V42H28" fill="none" strokeWidth="5" style={{ stroke: "var(--ico-body, #0E2433)" }} />
                <rect x="10" y="21.5" width="22" height="5" style={{ fill: "var(--ico-body, #0E2433)" }} />
                <path d="M4 24l11-10v20z" style={{ fill: "var(--ico-accent, #10B89A)" }} />
              </svg>
              <span className="signout-text">{t("auth.signOut")}</span>
            </button>
          </div>
        </header>
        <nav className={`sidebar${menuOpen ? " open" : ""}`} aria-label={t("nav.main")} id="main-menu">
          {NAV.filter((key) => (!NAV_FEATURE[key] || featureOn(workshop, NAV_FEATURE[key]!)) && (!role || allowed(key))).map((key) => (
            <a
              key={key}
              href={`#${key}`}
              className={`nav-${key}${key === page ? " active" : ""}`}
              aria-current={key === page ? "page" : undefined}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                go(key);
              }}
            >
              <NavIcon name={key} />
              <span>{t(`nav.${key}`)}</span>
              {NAV_PLAN_TAG[key] && <PlanTag featureKey={NAV_PLAN_TAG[key]!} />}
            </a>
          ))}
          <div className="menu-lang">
            <LanguageSelect />
          </div>
          <a className="legal-link" href={t("helpCenter.url")} target="_blank" rel="noopener noreferrer">
            {t("helpCenter.link")}
          </a>
          <a
            href="#legal"
            className="legal-link"
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              go("legal");
            }}
          >
            {t("legal.page.title")}
          </a>
          {operator && (
            <a
              href="#operator"
              className={`legal-link operator-link${page === "operator" ? " active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                go("operator");
              }}
            >
              {t("operator.title")}
            </a>
          )}
          <a
            href="#feedback"
            className={`nav-feedback${page === "feedback" ? " active" : ""}`}
            aria-current={page === "feedback" ? "page" : undefined}
            onClick={(e) => {
              e.preventDefault();
              setMenuOpen(false);
              go("feedback");
            }}
          >
            <NavIcon name="feedback" />
            <span>{t("nav.feedback")}</span>
          </a>
          <label className="help-switch">
            <span className="help-mark" aria-hidden>
              ?
            </span>
            <span className="help-switch-text">{t("help.switch")}</span>
            <input type="checkbox" checked={helpEnabled} onChange={(e) => void setHelpEnabled(e.target.checked)} aria-label={t("help.switch")} />
          </label>
        </nav>
        {phone && <nav className="bottombar" aria-label={t("nav.phone")}>
          {PHONE_NAV.map((key) => (
            <a
              key={key}
              href={`#${key}`}
              className={`nav-${key}${key === page && !menuOpen ? " active" : ""}`}
              aria-current={key === page ? "page" : undefined}
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                go(key);
              }}
            >
              <NavIcon name={key} />
              <span>{t(`nav.${key}`)}</span>
            </a>
          ))}
          <button type="button" className={menuOpen ? "active" : undefined} aria-expanded={menuOpen} aria-controls="main-menu" onClick={() => setMenuOpen((o) => !o)}>
            <svg className="gi" viewBox="0 0 48 48" width="30" height="30" aria-hidden="true" focusable="false">
              <rect x="6" y="9" width="36" height="5" style={{ fill: "var(--ico-body, #0E2433)" }} />
              <rect x="6" y="21.5" width="36" height="5" style={{ fill: "var(--ico-accent, #10B89A)" }} />
              <rect x="6" y="34" width="36" height="5" style={{ fill: "var(--ico-body, #0E2433)" }} />
            </svg>
            <span>{t("nav.more")}</span>
          </button>
        </nav>}
        <main className="content">
          {store.mode === "demo" && <p className="note">{t("demo.banner")}</p>}
          {askHelp && (
            <div className="help-ask" role="status">
              <span className="help-mark" aria-hidden>
                ?
              </span>
              <p>
                <strong>{t("help.askTitle")}</strong> {t("help.askText")}
              </p>
              <button type="button" className="primary" onClick={() => void setHelpEnabled(false)}>
                {t("help.turnOff")}
              </button>
              <button type="button" onClick={() => void store.savePrefs({ helpAsked: true }).then(setPrefs)}>
                {t("help.keep")}
              </button>
            </div>
          )}
          {billing === "overdue" && <p className="billing-notice">{t("billing.overdueNotice")}</p>}
          {subscription && page !== "subscription" && <TrialReminder sub={subscription} isOwner={role === "owner"} onChoose={() => go("subscription")} />}
          {page === "dashboard" && <Home />}
          {page === "workOrders" && <WorkOrders openId={route.id} />}
          {page === "customers" && <Customers openId={route.id} />}
          {page === "vehicles" && <Vehicles openId={route.id} />}
          {page === "reminders" && <Reminders />}
          {page === "import" && <Import />}
          {page === "settings" && <Settings user={user} />}
          {page === "subscription" && <Subscription />}
          {page === "inventory" && <Inventory openId={route.id} />}
          {page === "legal" && <Legal openId={route.id} />}
          {page === "feedback" && <Feedback from={cameFrom} />}
          {page === "operator" && operator && (mfa.status.factor && !mfa.status.verified ? (
            // a remembered device (aal1): the operator pages always need the code, asked here, not at sign in
            <section className="mfa-operator-stepup" aria-labelledby="operator-stepup-title">
              <h1 id="operator-stepup-title">{t("operator.title")}</h1>
              <StepUpForm store={store} userId={user.id} intro={t("mfa.stepUp.operator")} onDone={() => void reloadMfa()} />
            </section>
          ) : (
            <Operator openId={route.id} />
          ))}
          {page === "invoices" && (
            <>
              <h1>{t(`nav.${page}`)}</h1>
              <p className="muted">{t("common.comingSoon")}</p>
            </>
          )}
          {current?.status === "machine" && <p className="note">{t("manual.machineTranslated")}</p>}
        </main>
      </div>
      {billing === "suspended" && !operator && (
        <div className="modal-backdrop">
          <div className="suspended-gate" role="alertdialog" aria-labelledby="suspended-title">
            <h2 id="suspended-title">{t("billing.suspendedTitle")}</h2>
            <p>{t("billing.suspendedText")}</p>
            <button type="button" onClick={() => void signOutFresh(store)}>{t("auth.signOut")}</button>
          </div>
        </div>
      )}
      <QuoteNotices />
      <IntakeNotices />
      <StepUpDialog store={store} userId={user.id} onDone={() => void reloadMfa()} />
      {role === "owner" && termsOk === false && (
        <TermsGate
          onAccept={async (details) => {
            await store.acceptTerms(workshop.id, TERMS_VERSION, details);
            setTermsOk(true);
          }}
          onSignOut={() => void store.signOut()}
        />
      )}
    </AppContext.Provider>
  );
}
